import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { rockefeller } from '../../../../system/environment/Config.json'

export const MEETING_SCHEDULER_API_BASE = `${rockefeller.api.baseurl}/meetscheduler/api/meetingscheduler`

export interface BookingSlot {
  date: string
  startTime: string
  endTime: string
}

// Returned by POST /booking/session — the long-lived link token is exchanged for a
// short-lived session token. The nonce is issued by the first available-slots read.
export interface SessionResponse {
  sessionToken: string
}

export interface AvailableSlotsResponse {
  slots: BookingSlot[]
  isRebook: boolean
  timezoneAbbreviation?: string
  physicalLocation?: string | null
  // Client-selectable modes for this engagement/circle. null/absent = all modes allowed.
  allowedMeetingModes?: string[] | null
  currentDate?: string
  currentTime?: string
  currentMeetingMode?: string
  // Each read issues/rotates the nonce for this scope.
  bookingNonce: string
  nonceExpiresAt: string
}

export interface BookSlotRequest {
  sessionToken: string
  bookingNonce: string
  selectedDate: string
  selectedStartTime: string
  meetingMode: string
}

export interface BookSlotResponse {
  date: string
  startTime: string
  meetingMode?: string
  message: string
  // No nonce is returned — a successful booking ends the session; to modify, reopen the link.
}

export const bookingApi = createApi({
  reducerPath: '@api/booking',
  baseQuery: fetchBaseQuery({
    baseUrl: MEETING_SCHEDULER_API_BASE
  }),
  endpoints: (builder) => ({
    // Exchange the long-lived link token (from the email ?token=) for a short-lived session token.
    // The link token goes in the Authorization header (required by APIM).
    createSession: builder.mutation<SessionResponse, string>({
      query: (linkToken) => ({
        url: '/booking/session',
        method: 'POST',
        headers: { Authorization: `Bearer ${linkToken}` }
      })
    }),
    getAvailableSlots: builder.query<AvailableSlotsResponse, string>({
      query: (sessionToken) => ({
        url: '/booking/available-slots',
        headers: { Authorization: `Bearer ${sessionToken}` }
      })
    }),
    bookSlot: builder.mutation<BookSlotResponse, BookSlotRequest>({
      query: ({ sessionToken, ...body }) => ({
        url: '/booking/book-slot',
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken}` },
        body
      })
    })
  })
})

export const {
  useCreateSessionMutation,
  useGetAvailableSlotsQuery,
  useLazyGetAvailableSlotsQuery,
  useBookSlotMutation
} = bookingApi
